"use client"

import * as React from "react"
import { cn } from "../../utils/cn"

interface HeaderProps {
    level?: 1 | 2 | 3 | 4 | 5
    title: string | React.ReactNode
    description?: string | React.ReactNode
    emoji?: React.ReactNode // Emoji, icon, etc.
    rightContent?: React.ReactNode
    id?: string
    className?: string
    textColor?: string // Custom text color class (e.g., "text-w", "text-b")
}

/**
 * Unified Header component for all content types.
 * Level 1: Page Title (32px)
 * Level 2: Section Title (22px, no emoji)
 * Level 3: Subsection Title with optional emoji (17px)
 * Level 4: Card/Detail Title (15px)
 * Level 5: Group Label (13px, uppercase)
 */
export function Header({
    level = 2,
    title,
    description,
    emoji,
    rightContent,
    id,
    textColor = "text-b", // Default text color
    className
}: HeaderProps) {
    const renderContent = () => {
        switch (level) {
            case 1:
                return (
                    <div className={cn("mb-10 md:mb-16", className)}>
                        <h1 className={cn("text-2xl md:text-[32px] font-bold break-words leading-tight", textColor)}>
                            {title}
                        </h1>
                        {description && (
                            <p className={cn("text-[15px] md:text-base break-words leading-relaxed", textColor === "text-w" ? "text-w6" : "text-b6")}>
                                {description}
                            </p>
                        )}
                    </div>
                )
            case 2:
                return (
                    <div id={id} className={cn("flex flex-col mb-6 mt-2", className)}>
                        <div className="flex items-center justify-between gap-4">
                            <h2 className={cn("text-[20px] md:text-[22px] leading-tight font-bold", textColor)}>
                                {title}
                            </h2>
                            {rightContent && <div className="flex items-center">{rightContent}</div>}
                        </div>
                        {description && (
                            <div className={cn("text-[15px] mt-1 leading-relaxed", textColor === "text-w" ? "text-w6" : "text-b6")}>
                                {description}
                            </div>
                        )}
                    </div>
                )
            case 3:
                return (
                    <div id={id} className={cn("flex items-center justify-between gap-4 mb-4 pt-4", className)}>
                        <div className="flex items-center gap-2.5">
                            {emoji && (
                                <div className="flex items-center justify-center shrink-0">
                                    <span className="text-xl leading-none">{emoji}</span>
                                </div>
                            )}
                            <h3 className={cn("font-bold text-[17px] text-b", textColor)}>{title}</h3>
                        </div>
                        {rightContent && <div className="flex items-center gap-2">{rightContent}</div>}
                    </div>
                )
            case 4:
                return (
                    <h3 className={cn("text-[15px] font-bold text-b mb-1", className)} id={id}>
                        {title}
                    </h3>
                )
            case 5:
                return (
                    <div id={id} className={cn("flex items-center justify-between mt-6 first:mt-0 mb-3 px-0.5", className)}>
                        <div className={cn("text-[13px] text-b6 uppercase tracking-wider", textColor)}>
                            {title}
                        </div>
                        {rightContent && <div className="flex items-center gap-2">{rightContent}</div>}
                    </div>
                )
            default:
                return null
        }
    }

    return renderContent()
}
